import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReturnOrderLine } from 'returnCenter-api';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';
import { TableRow } from 'components/ui/Table';
import { alignFlexStart, getCellPadding } from 'utils/helpers';

import ProductInfoCell from 'components/TableCells/ProductInfoCell';
import OrderLineNumberCell from 'components/TableCells/OrderLineNumberCell';
import ShippedQtyCell from 'components/TableCells/ShippedQtyCell';
import ReturnQtyCell from 'components/TableCells/ReturnQtyCell';
import ReasonCell from 'components/TableCells/ReasonCell';
import Text from 'components/ui/Text';
import ReturnsOnThisLineCell from 'components/TableCells/ReturnsOnThisLineCell';
import styled from 'styled-components';

interface UseBatchReturnTableRowsOptions {
    origin: string | null;
}

export const useBatchReturnTableRows = ({ origin }: UseBatchReturnTableRowsOptions) => {
    const { t } = useTranslation();

    const headers: React.ReactNode[] = useMemo(
        () => [
            t('orderLineNumber', '#'),
            t('product', 'Product'),
            t('shippedQty', 'Shipped'),
            t('returnQty', 'Return Qty'),
            t('reason', 'Reason'),
            t('comments', 'Comments'),
            t('returns', 'Returns'),
        ],
        [t],
    );

    const getRows = useCallback(
        (orderId: string, orderLines: ReturnOrderLine[]): TableRow[] => {
            return orderLines.flatMap(
                (orderline) =>
                    orderline.returnItems?.map((returnItem: ExtendedReturnItem, idx) => {
                        const itemId = returnItem.id ?? returnItem.splitId;

                        return {
                            key: String(itemId ?? `${orderId}-${orderline.orderLineNumber}-${idx}`),
                            cells: [
                                {
                                    el: idx === 0 && (
                                        <OrderLineNumberCell orderLineNumber={orderline.orderLineNumber} />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: idx === 0 && <ProductInfoCell item={orderline} orderNumber={orderId} />,
                                    align: alignFlexStart(returnItem),
                                    ean: orderline.ean ?? '',
                                    sku: orderline.sku ?? '',
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ShippedQtyCell
                                            item={returnItem}
                                            canSplit={false}
                                            orderline={orderline}
                                            idx={idx}
                                            disabled
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: (
                                        <ReturnQtyCell
                                            item={returnItem}
                                            orderline={orderline}
                                            itemId={itemId}
                                            disabled
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: <ReasonCell item={returnItem} orderline={orderline} itemId={itemId} disabled />,
                                    align: alignFlexStart(returnItem),
                                    flex: 1,
                                    padding: getCellPadding(idx),
                                },
                                {
                                    el: <StyledText>{returnItem.comment || '-'}</StyledText>,
                                },
                                {
                                    el: idx === 0 && (
                                        <ReturnsOnThisLineCell
                                            returns={orderline.returnsOnThisLine ?? []}
                                            origin={origin}
                                        />
                                    ),
                                    align: alignFlexStart(returnItem),
                                    padding: getCellPadding(idx),
                                },
                            ],
                            removeGap: idx !== 0,
                        };
                    }) ?? [],
            );
        },
        [origin],
    );

    return { headers, getRows };
};

export default useBatchReturnTableRows;

const StyledText = styled(Text)`
    font-family: inherit;
    font-size: 13px;
`;
